import React, { useEffect } from 'react';
import BaseModal from '../utils/BaseModal/BaseModal';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import {
  ButtonWrapper,
  ButtonWrapperRow,
  CancelButton,
  Column,
  DeleteButton,
  Form,
  SubmitButton,
} from './CreateOrganizationMemberModal.styles';
import Input from '../../utils/inputs/Input/Input';
import SelectAlt from '../../utils/SelectAlt/SelectAlt';
import { CRM_ORGANIZATION_MEMBER_ROLE } from '../../../utils/constants';
import { splitByUppercase } from '../../../utils/util';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectChatterActionsPending,
  selectChattersOptions,
  selectInfluencerOptions,
  selectTeamLeadsOptions,
} from '../../../store/slices/chatter/slice';
import {
  createOrganizationMember,
  deleteOrganizationMember,
  editOrganizationMember,
  fetchAllOrganizationChatters,
  fetchAllOrganizationInfluencers,
  fetchAllOrganizationTeamLeads,
  getOrganizationMemberById,
} from '../../../store/slices/chatter/asyncThunks';
import { notifyError, notifyInfo } from '../../../utils/notify';
import { selectUser } from '../../../store/slices/user/slice';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/trash-1.svg';
import { useConfirmationModal } from '../ConfirmationModal/ConfirmationModal';
import Spinner from '../../Spinner/Spinner';

const createOrganizationMemberFormSchema = yup.object({
  id: yup.mixed().nullable(),
  name: yup.string().trim().required('Name is required'),
  email: yup.string().trim().required('Email is required').email('Invalid email'),
  password: yup
    .string()
    .trim()
    .when('id', {
      is: id => !id,
      then: schema => schema.required('Password is required'),
      otherwise: schema => schema.notRequired(),
    }),
  role: yup.mixed().required('Role is required'),
  influencers: yup.array().min(1, 'At least one influencer is required'),
});

const CreateOrganizationMemberModal = ({ modalRef, onSuccess, setSelectedMemberId, selectedMemberId, resetData }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const influencerOptions = useSelector(selectInfluencerOptions);
  const chattersOptions = useSelector(selectChattersOptions);
  const teamLeadsOptions = useSelector(selectTeamLeadsOptions);
  const {
    createOrganizationMember: createOrganizationMemberPending,
    deleteOrganizationMember: deleteOrganizationMemberPending,
    getOrganizationMemberById: getOrganizationMemberByIdPending,
    editOrganizationMember: editOrganizationMemberPending,
  } = useSelector(selectChatterActionsPending);
  const [openModal, closeModal, setConfirmationModalLoader] = useConfirmationModal();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    watch,
  } = useForm({
    delayError: 300,
    defaultValues: {
      id: null,
      name: '',
      email: '',
      password: '',
      role: null,
      influencers: [],
      chatters: [],
      teamLead: null,
    },
    resolver: yupResolver(createOrganizationMemberFormSchema),
  });

  const roleWatch = watch('role');

  useEffect(() => {
    setConfirmationModalLoader(deleteOrganizationMemberPending);
  }, [deleteOrganizationMemberPending, setConfirmationModalLoader]);

  useEffect(() => {
    dispatch(fetchAllOrganizationInfluencers());
    dispatch(fetchAllOrganizationChatters());
    dispatch(fetchAllOrganizationTeamLeads());
  }, [dispatch]);

  useEffect(() => {
    if (selectedMemberId) {
      dispatch(getOrganizationMemberById({ memberId: selectedMemberId }))
        .unwrap()
        .then(({ member }) => {
          setValue('id', selectedMemberId);
          setValue('name', member.name);
          setValue('email', member.email);
          setValue('role', { value: member.role, label: splitByUppercase(member.role) });
          setValue(
            'influencers',
            member.influencers.map(i => ({ value: i._id, label: i.name })),
          );
          if (member.teamLead) {
            setValue('teamLead', { value: member.teamLead._id, label: member.teamLead.name });
          }
          if (member.chatters) {
            setValue(
              'chatters',
              member.chatters.map(i => ({ value: i._id, label: i.name })),
            );
          }
        })
        .catch(err => {
          notifyError(err.message);
        });
    }
  }, [selectedMemberId, dispatch, setValue]);

  const onSubmit = data => {
    const { name, email, password, role, influencers, chatters, teamLead } = data;
    if (selectedMemberId) {
      dispatch(
        editOrganizationMember({
          memberId: selectedMemberId,
          name,
          influencerIds: influencers.map(influencer => influencer.value),
          ...(chatters.length && { chatterIds: chatters.map(chatter => chatter.value) }),
          ...(teamLead && { teamLeadId: teamLead.value }),
        }),
      )
        .unwrap()
        .then(() => {
          onSuccess?.();
          resetData?.();
          reset();
          modalRef.current.hide();
          notifyInfo('Member edited successfully');
        })
        .catch(err => {
          notifyError(err.message);
        });
    } else {
      dispatch(
        createOrganizationMember({
          name,
          email,
          password,
          role: role.value,
          influencerIds: influencers.map(influencer => influencer.value),
          ...(chatters.length && { chatterIds: chatters.map(chatter => chatter.value) }),
          ...(teamLead && { teamLeadId: teamLead.value }),
        }),
      )
        .unwrap()
        .then(() => {
          onSuccess?.();
          resetData?.();
          reset();
          modalRef.current.hide();
          notifyInfo('Member created successfully');
        })
        .catch(err => {
          notifyError(err.message);
        });
    }
  };

  const onDeleteMember = () => {
    openModal(
      'Delete Member',
      `Are you sure? This action will permanently delete the member.`,
      'Delete',
      () => {
        dispatch(
          deleteOrganizationMember({
            memberId: selectedMemberId,
          }),
        )
          .unwrap()
          .then(() => {
            onSuccess?.();
            closeModal();
            modalRef.current.hide();
            notifyInfo('Member deleted successfully!');
          })
          .catch(err => {
            notifyError(err.message);
          });
      },
      'Cancel',
      () => {
        closeModal();
      },
    );
  };

  const loading = createOrganizationMemberPending || editOrganizationMemberPending;

  let roles = [];
  if (user.role === CRM_ORGANIZATION_MEMBER_ROLE.OWNER) {
    roles = [
      CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
      CRM_ORGANIZATION_MEMBER_ROLE.CONTENT_MANAGER,
      CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER,
      CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
      CRM_ORGANIZATION_MEMBER_ROLE.CHATTER,
    ];
  } else if (user.role === CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR) {
    roles = [
      CRM_ORGANIZATION_MEMBER_ROLE.CONTENT_MANAGER,
      CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER,
      CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
      CRM_ORGANIZATION_MEMBER_ROLE.CHATTER,
    ];
  } else if (user.role === CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD) {
    roles = [CRM_ORGANIZATION_MEMBER_ROLE.CHATTER];
  }

  const roleOptions = roles.map(i => ({ value: i, label: splitByUppercase(i) }));

  return (
    <BaseModal
      title="Create New Member"
      ref={modalRef}
      onClose={() => {
        modalRef.current.hide();
      }}
      onAfterClose={() => {
        setSelectedMemberId(null);
        reset();
      }}>
      {getOrganizationMemberByIdPending ? (
        <Spinner height={454} width={100} />
      ) : (
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Column>
            <Input
              label="Name"
              placeholder="Enter name"
              {...register('name')}
              error={errors.name?.message}
              disabled={loading}
            />
            <Input
              disabled={loading || selectedMemberId}
              label="Email"
              placeholder="Enter email"
              {...register('email')}
              error={errors.email?.message}
            />
            {!selectedMemberId && (
              <Input
                disabled={loading}
                label="Password"
                placeholder="Enter password"
                {...register('password')}
                error={errors.password?.message}
                type="password"
              />
            )}
            <Controller
              name="role"
              control={control}
              render={({ field }) => {
                return (
                  <SelectAlt
                    selectedOption={field.value}
                    handleChange={field.onChange}
                    label="Role"
                    error={errors.role?.message}
                    options={roleOptions}
                    disabled={loading || selectedMemberId}
                  />
                );
              }}
            />
            <Controller
              name="influencers"
              control={control}
              render={({ field }) => (
                <SelectAlt
                  placeholder="Select influencer"
                  label="Influencer"
                  options={influencerOptions}
                  selectedOption={field.value}
                  handleChange={value => field.onChange(value)}
                  error={errors.influencers?.message}
                  multiSelection
                />
              )}
            />
            {roleWatch?.value === CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD && (
              <Controller
                name="chatters"
                control={control}
                render={({ field }) => (
                  <SelectAlt
                    placeholder="Select chatters"
                    label="Chatters"
                    options={chattersOptions}
                    selectedOption={field.value}
                    handleChange={value => field.onChange(value)}
                    error={errors.chatters?.message}
                    multiSelection
                  />
                )}
              />
            )}
            {roleWatch?.value === CRM_ORGANIZATION_MEMBER_ROLE.CHATTER &&
              [CRM_ORGANIZATION_MEMBER_ROLE.OWNER, CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR].includes(user.role) && (
                <Controller
                  name="teamLead"
                  control={control}
                  render={({ field }) => (
                    <SelectAlt
                      placeholder="Select Team Lead"
                      label="Team Lead"
                      options={teamLeadsOptions}
                      selectedOption={field.value}
                      handleChange={value => field.onChange(value)}
                      error={errors.teamLead?.message}
                    />
                  )}
                />
              )}
          </Column>
          <ButtonWrapper>
            <ButtonWrapperRow>
              <CancelButton
                title="Cancel"
                onClick={() => modalRef.current.hide()}
                type="button"
                disabled={loading || deleteOrganizationMemberPending}
              />
              {selectedMemberId && (
                <DeleteButton
                  onClick={onDeleteMember}
                  type="button"
                  disabled={loading || deleteOrganizationMemberPending}>
                  <DeleteIcon /> Delete
                </DeleteButton>
              )}
            </ButtonWrapperRow>
            <SubmitButton type="submit" title="Save" disabled={deleteOrganizationMemberPending} isLoading={loading} />
          </ButtonWrapper>
        </Form>
      )}
    </BaseModal>
  );
};

export default CreateOrganizationMemberModal;
